import { useQuery } from '@apollo/client';
import { ADMIN_JOBS_QUERY } from '@/graphql/queries/jobs.graphql';

/**
 * Job State Enum (must match API)
 */
export enum JobState {
  CREATED = 'created',
  RETRY = 'retry',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Job Status Enum (must match API)
 */
export enum JobStatus {
  QUEUED = 'queued',
  DELAYED = 'delayed',
  IN_PROGRESS = 'in_progress',
  RETRYING = 'retrying',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Date Range Enum
 * Note: Must match GraphQL enum keys (not internal values)
 */
export enum DateRange {
  LAST_DAY = 'LAST_DAY',
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_90_DAYS = 'LAST_90_DAYS',
}

/**
 * Job Filters Input
 */
export interface JobFiltersInput {
  name?: string;
  state?: JobState;
  states?: JobState[];
  stateCategory?: 'pending' | 'active' | 'completed' | 'failed';
  search?: string;
  dateRange?: DateRange;
}

/**
 * Sort Direction
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Job Type
 */
export interface Job {
  id: string;
  name: string;
  state: JobState;
  status: JobStatus | null;
  priority: number;
  retryCount: number;
  retryLimit: number;
  retryDelay: number;
  retryBackoff: boolean;
  createdOn: string;
  startedOn: string | null;
  completedOn: string | null;
  startAfter: string;
  keepUntil: string;
  age: number | null;
  duration: number | null;
  data?: any; // Optional - only included in detail view
  output?: any; // Optional - only included in detail view
}

/**
 * Admin Jobs Query Response
 */
export interface AdminJobsResponse {
  adminJobs: {
    jobs: Job[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

/**
 * Admin Jobs Query Variables
 */
export interface AdminJobsVariables {
  page?: number;
  limit?: number;
  filters?: JobFiltersInput;
  sortBy?: string;
  sortOrder?: SortDirection;
}

/**
 * Apollo Client hook for fetching jobs from pg-boss queue
 * Feature: 073-job-queue-inspector
 *
 * @param variables - Query variables (page, limit, filters, sorting)
 * @returns Query result with jobs data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data, loading, error, refetch } = useAdminJobsGraphQL({
 *   page: 0,
 *   limit: 50,
 *   filters: { stateCategory: 'active' },
 *   sortBy: 'createdOn',
 *   sortOrder: 'desc',
 * });
 * ```
 */
export function useAdminJobsGraphQL(variables: AdminJobsVariables = {}) {
  return useQuery<AdminJobsResponse, AdminJobsVariables>(ADMIN_JOBS_QUERY, {
    variables: {
      page: 0,
      limit: 50,
      sortBy: 'createdOn',
      sortOrder: 'desc',
      ...variables,
    },
    fetchPolicy: 'network-only', // Always fetch fresh data for admin tool
    notifyOnNetworkStatusChange: true, // Update loading state on refetch
  });
}
